import { useCallback, useEffect, useRef, useState } from 'react';
import { useFig } from '../../session/FigProvider.js';
import { Pager } from '../../core/api/query.js';
import type { ConversationListItem } from '../../core/api/types.js';
import { EmptyState, ErrorState, OfflineNotice, RowSkeleton } from '../../components/States.js';
import { Filters, SortControl } from '../../query/Filters.js';
import { useQueryState } from '../../query/useQueryState.js';

/**
 * The portal's conversation list.
 *
 * Two things here are requirements rather than choices:
 *   - the skeleton matches the real row layout, so the list does not jump when data lands
 *   - every row announces its position and the total, because virtualisation (needed at
 *     50,000 rows) otherwise tells a screen reader there are twenty items in the world
 */
export function PortalConversations() {
  const { client } = useFig();
  const query = useQueryState();
  const [rows, setRows] = useState<readonly ConversationListItem[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [total, setTotal] = useState<number | null>(null);
  const pagerRef = useRef(new Pager(query.sort, 25));

  // Depends on the SERIALISED filters and sort rather than the objects, which are rebuilt on
  // every render. A fresh Pager per query is deliberate: a keyset cursor encodes the sort it
  // was issued for, so carrying one across a sort change is a 400 by design.
  const key = JSON.stringify([query.filters, query.sort]);
  const load = useCallback(async () => {
    setError(null);
    setRows(null);
    pagerRef.current = new Pager(query.sort, 25);
    try {
      const page = await client.listConversations(query.filters, pagerRef.current);
      setRows(page.items);
      setTotal(pagerRef.current.exhausted ? page.items.length : null);
    } catch (e) {
      setError(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, key]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  return (
    <main className="page">
      <h1 className="page-title">Your calls</h1>
      {offline && <OfflineNotice />}

      <Filters state={query} />
      <SortControl state={query} />

      {error !== null && <ErrorState error={error} onRetry={() => void load()} />}

      {error === null && rows === null && <RowSkeleton count={8} />}

      {error === null && rows?.length === 0 && (
        query.activeCount > 0 ? (
          <EmptyState
            title="Nothing matches those filters"
            body="Try widening the dates, or clearing a filter or two."
            action={<button className="btn" onClick={query.clear}>Clear all filters</button>}
          />
        ) : (
          <EmptyState
            title="No calls yet"
            body="When your agency’s assistant takes a call for you, it will appear here — usually within the hour."
          />
        )
      )}

      {error === null && rows && rows.length > 0 && (
        <ul className="rows">
          {rows.map((c, i) => (
            <li key={c.id} className="row">
              <a
                className="row-link"
                href={`/calls/${c.id}`}
                aria-label={`${c.summary ?? 'Call'}, ${describeDuration(c.durationSeconds)}, ${new Date(c.startedAt).toLocaleString()}${total ? `, ${i + 1} of ${total}` : ''}`}
              >
                <span className="row-title">{c.summary ?? 'Call'}</span>
                <span className="row-meta">
                  {c.sentiment && <Sentiment value={c.sentiment} />}
                  <span className="row-when">{new Date(c.startedAt).toLocaleString()}</span>
                  {!c.hasAudio && <span className="row-tag">Transcript only</span>}
                </span>
                {/* aria-hidden: the readable duration is already in the link's own label */}
                <span className="row-dur" aria-hidden="true">{formatDuration(c.durationSeconds)}</span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

function Sentiment({ value }: { value: 'Positive' | 'Neutral' | 'Negative' }) {
  const key = value === 'Positive' ? 'pos' : value === 'Negative' ? 'neg' : 'neu';
  // Never colour alone (WCAG 1.4.1): the word is always present, the tint only reinforces it.
  return <span className={`sentiment sentiment-${key}`}>{value}</span>;
}

const formatDuration = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/**
 * "3:07" announces as "three oh seven". A duration needs a spoken form distinct from its
 * printed one - small, invisible, and the difference between usable and merely operable.
 */
function describeDuration(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m === 0) return `${r} second${r === 1 ? '' : 's'}`;
  if (r === 0) return `${m} minute${m === 1 ? '' : 's'}`;
  return `${m} minute${m === 1 ? '' : 's'} ${r} second${r === 1 ? '' : 's'}`;
}
